import { Link } from "react-router-dom";
import "../../styles/HomeNew.css";
import YICUNav from "../../nav/YICU-Nav";

// ── Reusable sub-components ──────────────────────────────────────────────────

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: "1.25rem" }}>
      <h2 style={{ fontFamily: "var(--ff-head)", fontSize: "1.3rem", fontWeight: 800, color: "var(--ink)", marginBottom: ".25rem" }}>
        {title}
      </h2>
      {subtitle && <p style={{ fontSize: ".88rem", color: "var(--muted)" }}>{subtitle}</p>}
    </div>
  );
}

function NomineeCard({ person, index, showDonate }) {
  const colors = ["lchip--purple", "lchip--pink", "lchip--green"];
  const c = colors[index % 3];
  const bd = c === "lchip--purple" ? "var(--pu-200)" : c === "lchip--pink" ? "var(--pk-200)" : "var(--gn-200)";
  const bg = c === "lchip--purple" ? "var(--pu-50)" : c === "lchip--pink" ? "var(--pk-50)" : "var(--gn-50)";
  const ac = c === "lchip--purple" ? "var(--pu-500)" : c === "lchip--pink" ? "var(--pk-500)" : "var(--gn-500)";

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "auto 1fr", gap: "1.1rem", alignItems: "flex-start",
      background: "var(--white)", border: "1.5px solid var(--bdr)", borderRadius: "16px", padding: "1.2rem",
      transition: "border-color .2s, box-shadow .2s, transform .2s",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = bd; e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,0,0,.07)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--bdr)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      {/* Avatar */}
      <div style={{ width: 72, height: 72, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `3px solid ${bd}`, background: bg }}>
        <img src={person.image} alt={person.name} width={72} height={72} loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={e => { e.target.style.display = "none"; }} />
      </div>
      {/* Content */}
      <div style={{ display: "flex", flexDirection: "column", gap: ".35rem" }}>
        <h3 style={{ fontFamily: "var(--ff-head)", fontSize: ".97rem", fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 }}>
          {person.name}
        </h3>
        {person.nonprofit && (
          <span className={`lcard__chip ${c}`} style={{ fontSize: ".58rem", width: "fit-content" }}>
            Supports: {person.nonprofit}
          </span>
        )}
        {person.bio && <p style={{ fontSize: ".82rem", color: "var(--muted)", lineHeight: 1.7 }}>{person.bio}</p>}
        {showDonate && person.donateUrl && (
          <a href={person.donateUrl} target="_blank" rel="noopener noreferrer"
            style={{
              fontSize: ".75rem", fontWeight: 700, color: ac, textDecoration: "none", width: "fit-content",
              border: `1.5px solid ${bd}`, borderRadius: "100px", padding: ".28em .8em", marginTop: ".2rem",
              transition: "background .15s"
            }}
            onMouseEnter={e => { e.currentTarget.style.background = bg; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
          >
            Donate → Stand. Ovation Award
          </a>
        )}
      </div>
    </div>
  );
}

function PhotoCard({ src, caption, wide }) {
  return (
    <div className="lcard" style={{ padding: 0, overflow: "hidden", gap: 0, gridColumn: wide ? "1 / -1" : undefined }}>
      <img src={src} alt={caption || ""} loading="lazy"
        style={{ width: "100%", display: "block", maxHeight: wide ? 400 : 280, objectFit: "cover", borderRadius: "14px 14px 0 0" }} />
      {caption && (
        <div style={{ padding: ".75rem 1rem" }}>
          <p style={{ fontSize: ".82rem", color: "var(--muted)", lineHeight: 1.6, textAlign: "center" }}>{caption}</p>
        </div>
      )}
    </div>
  );
}

function AwardBlock({ label, items, color }) {
  const bg = color === "purple" ? "var(--pu-50)" : color === "pink" ? "var(--pk-50)" : "var(--gn-50)";
  const bd = color === "purple" ? "var(--pu-200)" : color === "pink" ? "var(--pk-200)" : "var(--gn-200)";
  const chip = color === "purple" ? "lchip--purple" : color === "pink" ? "lchip--pink" : "lchip--green";
  return (
    <div className="lcard" style={{ background: bg, borderColor: bd, gap: ".6rem" }}>
      <span className={`lcard__chip ${chip}`} style={{ fontSize: ".62rem", width: "fit-content" }}>{label}</span>
      {items.map((it, i) => (
        <p key={i} style={{ fontSize: ".88rem", color: "var(--muted)", lineHeight: 1.7 }}>{it}</p>
      ))}
    </div>
  );
}

function WinnerSection({ title, winners }) {
  if (!winners?.length) return null;

  return (
    <section className="local" style={{ maxWidth: "var(--max)", marginInline: "auto" }}>
      <SectionHeader title={title} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1.1rem" }}>
        {winners.map((winner, index) => (
          <div key={`${winner.name}-${index}`} className="lcard" style={{ padding: 0, overflow: "hidden", gap: 0 }}>
            {winner.image && (
              <img src={winner.image} alt={winner.name} loading="lazy"
                style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", display: "block" }} />
            )}
            <h3 style={{ padding: "1rem", fontFamily: "var(--ff-head)", fontSize: ".95rem", fontWeight: 800, color: "var(--ink)" }}>
              {winner.name}
            </h3>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function YICUWinnerPage({ year, logoSrc, wip = false, content }) {
  return (
    <div className="pg">

      {/* HERO */}
      <section className="hero" style={{ minHeight: "36svh" }}>
        <div className="hero__glow hero__glow--1" /><div className="hero__glow hero__glow--2" /><div className="hero__glow hero__glow--3" />
        <div className="hero__inner" style={{ gridTemplateColumns: "1fr auto" }}>
          <div className="hero__text">
            <p className="hero__kicker"><Link to="/yicu" style={{ color: "var(--pk-200)", textDecoration: "none", fontWeight: 700 }}>← YICU Service Awards</Link></p>
            <h1 className="hero__h1">YICU <span className="hero__accent">{year}</span></h1>
            <p className="hero__sub">
              {wip ? "This year's page is coming soon." : `Celebrating the ${year} YICU Service Award winners.`}
            </p>
          </div>
          <div className="hero__stats">
            {logoSrc && (
              <div className="hstat hstat--soft" style={{ alignItems: "center" }}>
                <img src={logoSrc} alt={`YICU ${year}`} style={{ maxWidth: 140, maxHeight: 70, objectFit: "contain", display: "block" }} />
              </div>
            )}
            <div className="hstat hstat--green"><span className="hstat__n">{year}</span><span className="hstat__l">Class Year</span></div>
          </div>
        </div>
      </section>

      {/* NAV */}
      <div style={{ maxWidth: "var(--max)", marginInline: "auto", padding: "0 var(--pad)" }}>
        <YICUNav />
      </div>

      {/* WIP */}
      {wip && (
        <section className="local" style={{ maxWidth: "600px", marginInline: "auto" }}>
          <div className="lcard" style={{ background: "var(--pk-50)", borderColor: "var(--pk-200)", alignItems: "center", padding: "2.5rem", gap: "1rem", textAlign: "center" }}>
            <span style={{ fontSize: "2.5rem" }}>🚧</span>
            <h3 style={{ fontFamily: "var(--ff-head)", fontSize: "1.2rem", fontWeight: 800, color: "var(--ink)" }}>Coming Soon</h3>
            <p style={{ fontSize: ".9rem", color: "var(--muted)", lineHeight: 1.7 }}>The YICU {year} page is being prepared.</p>
            <Link to="/yicu" className="lcard__toggle" style={{ textDecoration: "none", borderColor: "var(--pk-200)", color: "var(--pk-500)" }}>← Back to YICU</Link>
          </div>
        </section>
      )}

      {!wip && content && (
        <>
          <WinnerSection title="🏅 Individual Winners" winners={content.individualWinner} />
          <WinnerSection title="🌟 Community Hero Award" winners={content.communityHeroAwardWinner} />
          <WinnerSection title="👏 Standing Ovation Award" winners={content.standingOvationAwardWinner} />
          <WinnerSection title="👥 Team Winners" winners={content.teamWinners} />
          <WinnerSection title="📢 Advocate Winners" winners={content.advocateWinners} />
          <WinnerSection title="🤝 Mentor Winners" winners={content.mentorWinners} />

          {/* INDIVIDUAL NOMINEES */}
          {content.individualNominees && content.individualNominees.length > 0 && (
            <section id="nominees" className="local" style={{ maxWidth: "var(--max)", marginInline: "auto" }}>
              <div className="sec-hd">
                <span className="sec-eye">Individual Nominees</span>
                <h2 className="sec-h2">{year} Individual Nominees</h2>
                <p className="sec-sub">{content.individualNominees.length} young leaders nominated for the {year} YICU Service Awards.</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: ".85rem" }}>
                {content.individualNominees.map((p, i) => (
                  <NomineeCard key={p.name + i} person={p} index={i} showDonate={!!content.supportBanner} />
                ))}
              </div>
            </section>
          )}

          {/* TEAM NOMINEES */}
          {content.teamNominees && content.teamNominees.length > 0 && (
            <section className="local" style={{ maxWidth: "var(--max)", marginInline: "auto" }}>
              <SectionHeader title="👥 Team Nominees" />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.1rem" }}>
                {content.teamNominees.map((t, i) => (
                  <div key={i} className="lcard" style={{ padding: 0, overflow: "hidden", gap: 0 }}>
                    {t.image && <img src={t.image} alt={t.name} loading="lazy" style={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block", borderRadius: "14px 14px 0 0" }} />}
                    <div style={{ padding: "1rem 1.1rem", display: "flex", flexDirection: "column", gap: ".4rem" }}>
                      <h3 style={{ fontFamily: "var(--ff-head)", fontSize: ".95rem", fontWeight: 800, color: "var(--ink)" }}>{t.name}</h3>
                      {t.bio && <p style={{ fontSize: ".82rem", color: "var(--muted)", lineHeight: 1.65 }}>{t.bio}</p>}
                      {t.nonprofit && <span className="lcard__chip lchip--pink" style={{ fontSize: ".58rem", width: "fit-content" }}>Supports: {t.nonprofit}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ADVOCATE NOMINEES */}
          {content.advocateNominees && content.advocateNominees.length > 0 && (
            <section className="local" style={{ maxWidth: "var(--max)", marginInline: "auto" }}>
              <SectionHeader title="📢 Advocate Nominees" />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.1rem" }}>
                {content.advocateNominees.map((a, i) => (
                  <div key={i} className="lcard" style={{ padding: 0, overflow: "hidden", gap: 0 }}>
                    {a.image && <img src={a.image} alt={a.name} loading="lazy" style={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block", borderRadius: "14px 14px 0 0" }} />}
                    <div style={{ padding: "1rem 1.1rem", display: "flex", flexDirection: "column", gap: ".4rem" }}>
                      <h3 style={{ fontFamily: "var(--ff-head)", fontSize: ".95rem", fontWeight: 800, color: "var(--ink)" }}>{a.name}</h3>
                      {a.bio && <p style={{ fontSize: ".82rem", color: "var(--muted)", lineHeight: 1.65 }}>{a.bio}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* MENTOR NOMINEES */}
          {content.mentorNominees && content.mentorNominees.length > 0 && (
            <section className="local" style={{ maxWidth: "860px", marginInline: "auto" }}>
              <SectionHeader title="🌟 Mentor Nominees" />
              <div style={{ display: "flex", flexDirection: "column", gap: ".85rem" }}>
                {content.mentorNominees.map((m, i) => (
                  <NomineeCard key={m.name + i} person={m} index={i} showDonate={false} />
                ))}
              </div>
            </section>
          )}

          {/* NOMINEE LIST (text-only, older years) */}
          {content.nomineeList && content.nomineeList.length > 0 && (
            <section className="local" style={{ maxWidth: "var(--max)", marginInline: "auto" }}>
              <SectionHeader title="📋 All Nominees" />
              <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem" }}>
                {content.nomineeList.map((n, i) => (
                  <span key={i} className={`lcard__chip ${["lchip--purple", "lchip--pink", "lchip--green"][i % 3]}`}
                    style={{ fontSize: ".78rem", padding: ".35em .9em" }}>{n}</span>
                ))}
              </div>
            </section>
          )}

          {/* PHOTO GALLERY */}
          {content.photos && content.photos.length > 0 && (
            <section className="local" style={{ maxWidth: "860px", marginInline: "auto" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
                {content.photos.map((p, i) => <PhotoCard key={i} src={p.src} caption={p.caption} wide={p.wide} />)}
              </div>
            </section>
          )}
        </>
      )}

    </div>
  );
}